import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { usePostData } from "../../../../hooks";
import { EndPoints } from "../../../../Constants/endpoints";
import { refetchAllAppointmentsApis } from "../../../../api/Appointments/refetchAllAppointmentsApis";
import { GeneralButton, Modal } from "../../../../components";
import { useToast } from "../../../../Services/ToastContext";

interface RemovePatientProps {
  navigate?: () => void;
  closeDeleteModal: () => void;
  isDeleteModalOpen: boolean;
  name: string;
  id: number | string;
}

function RemovePatient({
  navigate,
  closeDeleteModal,
  isDeleteModalOpen,
  name,
  id,
}: RemovePatientProps) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const { mutate } = usePostData(`${EndPoints.UnlinkPatient}/${Number(id)}`);
  const handleDelete = () => {
    mutate(
      {},
      {
        onError: (error) =>
          showToast(error.message || t("failed_to_delete"), "error"),
        onSuccess: () => {
          showToast(t("deleted_successfully"), "success");
          closeDeleteModal();
          if (navigate) {
            navigate();
          }
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetMyPatient],
            exact: false,
          });
          refetchAllAppointmentsApis(queryClient);
        },
      },
    );
  };

  return (
    <Modal
      header={t("remove_patient")}
      isOpen={isDeleteModalOpen}
      onClose={closeDeleteModal}
      customStyle="max-w-[500px]"
      width="max-w-xl"
    >
      <div className="text-body-text text-lg">
        <span>{t("are_you_sure_that_you_want_to_delete")}</span>
        <span className="w- text-primary-text mx-2 font-semibold" dir="rtl">
          {name}
        </span>
        <span>{t("from_your_list")}</span>
      </div>
      <div className="mt-5 flex max-w-md gap-2 self-end">
        <GeneralButton
          title={t("cancel")}
          color="lite"
          onClick={closeDeleteModal}
        />
        <GeneralButton
          type="submit"
          title={t("remove")}
          color="liteRed"
          onClick={handleDelete}
        />
      </div>
    </Modal>
  );
}

export default RemovePatient;
